import { Cookie } from '../../utils/cookie';

/**
 * The cookie that carries the Atlassian Account ID on WAC. Mirrors
 * `ATLASSIAN_ACCOUNT_ID_COOKIE_KEY` in `@/feature-gates/bootstrapper`; kept as a
 * local constant so this client-side analytics util does not pull in the
 * feature-gate bootstrap module.
 */
export const ATLASSIAN_ACCOUNT_ID_COOKIE_KEY = '__aid_user_id';

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Atlassian Account IDs occur in three real-world shapes:
 *   - `<directoryId>:<uuid>`  e.g. `712020:0e5f1e3c-1a2b-4c3d-8e9f-0a1b2c3d4e5f` (current identity format)
 *   - `<uuid>`                e.g. `0e5f1e3c-1a2b-4c3d-8e9f-0a1b2c3d4e5f`
 *   - `<24-hex>`              legacy ObjectId-style ids
 *
 * Anything else (Segment/anonymous tokens, empty strings, mangled cookie
 * values) is NOT an AAID and must never be labelled as one downstream.
 */
const ATLASSIAN_ACCOUNT_ID_RE = new RegExp(`^(?:\\d+:)?${UUID}$|^[0-9a-fA-F]{24}$`);

/**
 * Returns true only when `value` matches a known Atlassian Account ID shape.
 * Use this as the single source of truth before treating any value as an AAID.
 */
export const isValidAtlassianAccountId = (value: string | null | undefined): value is string =>
	typeof value === 'string' && ATLASSIAN_ACCOUNT_ID_RE.test(value.trim());

/**
 * Reads the `__aid_user_id` cookie and returns it only if it is a valid
 * Atlassian Account ID; otherwise returns `null`.
 *
 * Why this exists: the raw cookie is populated by upstream systems and, on
 * anonymous / pre-auth traffic (e.g. logged-out `/try/cloud/signup`), can hold
 * a non-AAID token. Consumers historically read the cookie and asserted it was
 * an AAID — feeding GASv3 `setUserInfo(ATLASSIAN_ACCOUNT, …)` and the OneTrust →
 * ConsentHub sync — which ConsentHub then rejects with HTTP 400
 * ("invalid userId and userIdType pair"). Routing every read through this
 * accessor makes that class of bug impossible.
 */
export const getValidatedAtlassianAccountId = (requestCookie?: string): string | null => {
	const raw = Cookie.readCookie(ATLASSIAN_ACCOUNT_ID_COOKIE_KEY, requestCookie);
	if (!raw) {
		return null;
	}
	let decoded: string;
	try {
		decoded = decodeURIComponent(raw).trim();
	} catch {
		return null;
	}
	return isValidAtlassianAccountId(decoded) ? decoded : null;
};
